import React from 'react';
import axiosInstance, { endpoints } from '@/lib/axios';
import type { ILeadPayload, LeadOrigin } from '@/types/lead';

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    origin?: LeadOrigin;
    message?: string;
    /** Formation-specific callers (see Fiche/FicheStickyCta) — without these the
     *  lead lands with nothing in the admin's "Formation" / "Thème" columns. */
    formationTitle?: string;
    formationTheme?: string;
}

interface ContactForm {
    entreprise: string;
    prenom: string;
    nom: string;
    email: string;
    /** Optional — sent only when filled (see the payload below). */
    phone: string;
    /** Pre-filled from `formationTitle`, editable → `leads.formation_title`. */
    formation: string;
    /** Free text (a region *or* a city) → `leads.city`. */
    region: string;
    periode: string;
}

const EMPTY_FORM: ContactForm = {
    entreprise: '',
    prenom: '',
    nom: '',
    email: '',
    phone: '',
    formation: '',
    region: '',
    periode: '',
};

/** The `leads` table has no column for this, so it goes in the message. */
const PERIODES = [
    'Dans le mois',
    'Dans les 3 mois',
    'Dans les 6 mois',
    'Pas de contrainte',
] as const;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INPUT_CLASS =
    'w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]';

/** "06 12 34 56 78" → "+33612345678". Empty stays empty: the field is optional. */
function normalizePhone(value: string): string {
    const compact = value.replace(/[\s.\-()]/g, '');
    if (!compact) return '';
    if (compact.startsWith('+')) return compact;
    if (compact.startsWith('0')) return `+33${compact.slice(1)}`;
    return `+33${compact}`;
}

const ContactModal: React.FC<ContactModalProps> = ({
    isOpen,
    onClose,
    origin = 'contact',
    message = "Demande d'orientation formation depuis la page d'accueil.",
    formationTitle,
    formationTheme,
}) => {
    const [formData, setFormData] = React.useState<ContactForm>(EMPTY_FORM);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    // Pre-fill the formation on open — the fiche callers know it, the home /
    // FAQ ones don't and leave the field for the visitor to fill in.
    React.useEffect(() => {
        if (isOpen) {
            setFormData(prev => ({ ...prev, formation: formationTitle ?? '' }));
        }
    }, [isOpen, formationTitle]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Only Prénom / Nom / Email are required — the four fields below them are
    // qualification bonuses, not gates.
    const isFormValid =
        formData.prenom.trim() !== '' &&
        formData.nom.trim() !== '' &&
        EMAIL_PATTERN.test(formData.email.trim());

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid || submitting) return;

        setSubmitting(true);
        setError('');
        try {
            const phone = normalizePhone(formData.phone);
            const payload: ILeadPayload = {
                first_name: formData.prenom.trim(),
                last_name: formData.nom.trim(),
                email: formData.email.trim(),
                phone: phone || undefined,
                company: formData.entreprise.trim() || undefined,
                city: formData.region.trim() || undefined,
                message: [
                    message,
                    formData.periode && `Période souhaitée : ${formData.periode}`,
                ]
                    .filter(Boolean)
                    .join('\n'),
                formation_title: formData.formation.trim() || undefined,
                // Capped: the API validates formation_theme at 150 characters.
                formation_theme: formationTheme?.slice(0, 150),
                origin,
            };

            await axiosInstance.post(endpoints.leads.create, payload);
            setFormData(EMPTY_FORM);
            onClose();
        } catch {
            setError("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#FFFFFF4D] backdrop-blur-xs">
            <div className="relative w-full max-w-170 max-h-[90vh] flex flex-col bg-white rounded-lg border border-[#40BFDE]" style={{ boxShadow: "0px 1px 2px 1px #00000014" }}>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    type="button"
                    aria-label="Fermer"
                    className="absolute top-5 right-5 z-10 text-[#1B365E] hover:text-[#40BFDE] transition-colors cursor-pointer duration-75"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_40006002_6942)">
                            <path d="M23.2897 4.0987C24.2268 3.16164 24.2268 1.63986 23.2897 0.702796C22.3526 -0.234265 20.8309 -0.234265 19.8938 0.702796L12 8.60409L4.0987 0.710292C3.16164 -0.226769 1.63986 -0.226769 0.702796 0.710292C-0.234265 1.64735 -0.234265 3.16914 0.702796 4.1062L8.60409 12L0.710292 19.9013C-0.226769 20.8384 -0.226769 22.3601 0.710292 23.2972C1.64735 24.2343 3.16914 24.2343 4.1062 23.2972L12 15.3959L19.9013 23.2897C20.8384 24.2268 22.3601 24.2268 23.2972 23.2897C24.2343 22.3526 24.2343 20.8309 23.2972 19.8938L15.3959 12L23.2897 4.0987Z" fill="currentColor" />
                        </g>
                        <defs>
                            <clipPath id="clip0_40006002_6942">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </button>

                <div className="overflow-y-auto pt-14 pb-6 sm:pb-12 px-5 sm:px-10">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <h2 className="font-red-hat font-light text-[22px] sm:text-[30px] leading-7 sm:leading-10 text-[#1B365E] mb-4.5 tracking-[0.9px]">
                            Soyez prévenu dès qu&apos;une session <br className="hidden md:block" />
                            vous correspond
                        </h2>
                        <p className="font-medium text-[16px] sm:text-[20px] leading-5.5 sm:leading-7.5 text-[#1B365E] tracking-[0.4px]">
                            Laissez-nous vos coordonnées, nous vous recontactons avec une
                            solution adaptée ou dès qu&apos;une session est disponible.
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-6 max-w-103 mx-auto">
                        <div>
                            <input
                                type="text"
                                name="entreprise"
                                maxLength={255}
                                autoComplete="organization"
                                aria-label="Entreprise (optionnel)"
                                placeholder="Entreprise (optionnel)"
                                value={formData.entreprise}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div>
                            <input
                                type="text"
                                name="prenom"
                                autoComplete="given-name"
                                aria-label="Prénom"
                                placeholder="Prénom*"
                                value={formData.prenom}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div>
                            <input
                                type="text"
                                name="nom"
                                autoComplete="family-name"
                                aria-label="Nom"
                                placeholder="Nom*"
                                value={formData.nom}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div>
                            <input
                                type="email"
                                name="email"
                                autoComplete="email"
                                aria-label="Email"
                                placeholder="Email*"
                                value={formData.email}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div className="flex gap-2.5 sm:gap-4.5">
                            <div className="w-14 h-10 px-3 flex items-center justify-center rounded-lg border border-[#EFEFEF] bg-white text-[12px] font-normal text-[#ABABAB]">
                                +33
                            </div>
                            <input
                                type="tel"
                                name="phone"
                                inputMode="tel"
                                autoComplete="tel"
                                aria-label="Téléphone (optionnel)"
                                placeholder="Téléphone (optionnel)"
                                value={formData.phone}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div>
                            <input
                                type="text"
                                name="formation"
                                maxLength={255}
                                aria-label="Formation concernée"
                                placeholder="Formation concernée"
                                value={formData.formation}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div>
                            <input
                                type="text"
                                name="region"
                                maxLength={255}
                                aria-label="Région ou ville souhaitée (optionnel)"
                                placeholder="Région ou ville souhaitée (optionnel)"
                                value={formData.region}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div className="relative">
                            <select
                                name="periode"
                                aria-label="Période souhaitée"
                                value={formData.periode}
                                onChange={handleChange}
                                className={`${INPUT_CLASS} appearance-none pr-8 cursor-pointer ${formData.periode === '' ? 'text-[#ABABAB]' : ''}`}
                            >
                                <option value="">Période souhaitée</option>
                                {PERIODES.map(periode => (
                                    <option key={periode} value={periode} className="text-[#1B365E]">
                                        {periode}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5">
                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                                    <path d="M1 1L5 5L9 1" stroke="#1B365E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        <div className="text-[12px] text-[#1B365E] font-medium tracking-[0.25px]">
                            *Champs obligatoires
                        </div>

                        {error && <p className="text-[12px] text-red-600">{error}</p>}

                        <button
                            type="submit"
                            className={`w-full h-12 sm:h-14.5 border font-bold text-[16px] rounded-lg transition-all duration-300 ${isFormValid && !submitting
                                ? "bg-[#1B365E] border-[#1B365E] text-white hover:bg-[#152b4d] cursor-pointer"
                                : "bg-[#D9D9D9] border-[#ABABAB] text-[#9CA3AF] cursor-not-allowed"
                                }`}
                            disabled={!isFormValid || submitting}
                        >
                            {submitting ? "Envoi..." : "Me tenir informé"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContactModal;
